import { http } from '@/base/utils/http-compat'

const OPTIONS = {
  credentials: 'include',
}

function isResponseValidString(response) {
  return typeof response.body === 'string'
}
function isResponseValidObject(response) {
  return typeof response.body === 'object'
}
export class ApiClass {
  /**
   * GET pod adres bezwzględny spoza naszego backendu.
   *
   * ⚠️ W przeciwieństwie do pozostałych metod opcje wołającego mają tu **pierwszeństwo** przed
   * `OPTIONS`. Powód: `credentials: 'include'` przy żądaniu cross-origin wymaga od serwera
   * `Access-Control-Allow-Credentials: true` i konkretnego origin - a fallback REST statusu PIS
   * (`wtn.bm.pl`) tego nagłówka nie oddaje, więc przeglądarka odrzucała żądanie `TypeError`-em
   * (pomiar 03.09.2026). Adres jest tam podpisany per transakcja, więc ciasteczka i tak nie są
   * do niczego potrzebne - wołający musi móc powiedzieć `credentials: 'omit'`.
   *
   * Kolejność scalania nie zmienia zachowania żadnego istniejącego wywołania: `credentials`
   * przekazuje wyłącznie `PisTransactionStatusClass`.
   *
   * @param {string} url
   * @param {object} [options]
   * @returns {Promise}
   */
  getExternal(url, options = {}) {
    options = { ...OPTIONS, ...options }
    return http.get(url, options).then(
      (response) => {
        if (isResponseValidString(response) || isResponseValidObject(response)) {
          return Promise.resolve(response)
        }
        return Promise.reject(response)
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  get(endpoint, options = {}) {
    Object.assign(options, OPTIONS)
    const url = ApiClass.getBackendUrl(endpoint)
    return http.get(url, options).then(
      (response) => {
        if (isResponseValidString(response) || isResponseValidObject(response)) {
          return Promise.resolve(response)
        }
        return Promise.reject(response)
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  getAds(endpoint, options = {}) {
    Object.assign(options, {
      credentials: false,
    })
    const url = ApiClass.getAdServerUrl(endpoint)
    return http.get(url, options).then(
      (response) => {
        if (isResponseValidString(response) || isResponseValidObject(response)) {
          return Promise.resolve(response)
        }
        return Promise.reject(response)
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  post(endpoint, data = {}, options = {}) {
    Object.assign(options, OPTIONS)
    const url = ApiClass.getBackendUrl(endpoint)
    return http.post(url, data, options).then(
      (response) => {
        if (isResponseValidString(response) || isResponseValidObject(response)) {
          return Promise.resolve(response)
        }
        return Promise.reject(response)
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  postCards(endpoint, data = {}, options = {}) {
    Object.assign(options, OPTIONS, { credentials: false })
    const url = ApiClass.getCardsBackendUrl(endpoint)
    return http.post(url, data, options).then(
      (response) => {
        if (isResponseValidString(response) || isResponseValidObject(response)) {
          return Promise.resolve(response)
        }
        return Promise.reject(response)
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  postAdForm(endpoint, data = {}, options = {}) {
    Object.assign(options, OPTIONS, { credentials: false })
    const url = ApiClass.getAdFormUrl(endpoint)
    return http.post(url, data, options).then(
      (response) => {
        if (isResponseValidString(response) || isResponseValidObject(response)) {
          return Promise.resolve(response)
        }
        return Promise.reject(response)
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  postSyncHub(endpoint, data = {}, options = {}) {
    Object.assign(options, OPTIONS, { credentials: false })
    const url = ApiClass.getSyncHubUrl(endpoint)
    return http.post(url, data, options).then(
      (response) => {
        if (isResponseValidString(response) || isResponseValidObject(response)) {
          return Promise.resolve(response)
        }
        return Promise.reject(response)
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  static getBackendUrl(endpoint) {
    if (import.meta.env.DEV) {
      return endpoint
    }

    const apiUrl = import.meta.env.VITE_APP_API
    return apiUrl ? `${apiUrl}${endpoint}` : endpoint
  }

  static getCardsBackendUrl(endpoint) {
    if (import.meta.env.DEV) {
      return endpoint
    }

    return import.meta.env.VITE_APP_CARDS_API + endpoint
  }

  static getAdServerUrl(endpoint) {
    if (import.meta.env.DEV) {
      return endpoint
    }

    return import.meta.env.VITE_APP_ADS_API + endpoint
  }

  static getAdFormUrl(endpoint) {
    if (import.meta.env.DEV) {
      return endpoint
    }

    return import.meta.env.VITE_APP_ADFORM_URL + endpoint
  }

  static getSyncHubUrl(endpoint) {
    const apiUrl = import.meta.env.VITE_APP_SYNCHUB_API
    return apiUrl ? `${apiUrl}${endpoint}` : endpoint
  }
}

export const apiClass = new ApiClass()
